import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Owner } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface OwnerState {
  owners: Owner[];
  owner: Owner | null;
  activeOwnerId: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: OwnerState = {
  owners: [],
  owner: null,
  activeOwnerId: null,
  loading: false,
  error: null,
};

// ✅ Helper pour récupérer l'owner depuis le localStorage (fallback)
const getOwnerFromCache = (): Owner | null => {
  try {
    const cached = localStorage.getItem('currentOwner');
    if (cached) {
      const owner = JSON.parse(cached);
      console.log('💾 Owner récupéré depuis le cache:', owner.id);
      return owner;
    }
  } catch (err) {
    console.error('❌ Erreur lecture cache owner:', err);
  }
  return null;
};

// ✅ Helper pour sauvegarder l'owner dans le localStorage
const saveOwnerToCache = (owner: Owner) => {
  try {
    localStorage.setItem('currentOwner', JSON.stringify(owner));
    console.log('💾 Owner sauvegardé en cache:', owner.id);
  } catch (err) {
    console.error('❌ Erreur sauvegarde cache owner:', err);
  }
};

// ✅ Fetch all owners
export const fetchowners = createAsyncThunk<Owner[], void, { state: RootState }>(
  'owner/fetchOwners',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/owners/`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` },
          timeout: 5000
        }
      );
      console.log('✅ Owners récupérés:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur fetchowners:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch owners');
    }
  }
);

// ✅ Fetch single owner avec fallback cache
export const fetchowner = createAsyncThunk<Owner, number, { state: RootState }>(
  'owner/fetchOwner',
  async (ownerId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      console.log('🔍 Fetching owner:', ownerId);
      
      const response = await axios.get(
        `${baseUrlApi}/owners/${ownerId}`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` },
          timeout: 5000
        }
      );
      
      console.log('✅ Owner récupéré:', response.data);
      
      const owner = Array.isArray(response.data) ? response.data[0] : response.data;
      
      // ✅ Sauvegarder en cache
      if (owner) {
        saveOwnerToCache(owner);
      }
      
      dispatch(fetchowners());
      return owner;
      
    } catch (error: any) {
      console.error('❌ Erreur fetchowner:', error.response?.data || error.message);
      
      // ✅ FALLBACK: Récupérer depuis le cache
      const cachedOwner = getOwnerFromCache();
      if (cachedOwner && cachedOwner.id === ownerId) {
        console.log('💾 Utilisation du cache pour owner:', ownerId);
        return cachedOwner;
      }
      
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch owner');
    }
  }
);

// ✅ Fetch owner by user ID avec fallback cache
// ✅ Remplacer encoursowner pour utiliser l'OwnerId directement
export const encoursowner = createAsyncThunk<Owner, void, { state: RootState }>(
  'owner/encoursowner',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const ownerId = authentification.user.OwnerId;
      
      if (!ownerId) {
        throw new Error('OwnerId manquant dans authentification');
      }
      
      console.log('🔍 Fetching owner:', ownerId);
      
      const response = await axios.get(
        `${baseUrlApi}/owners/${ownerId}`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` },
          timeout: 5000
        }
      );
      
      console.log('✅ Owner courant récupéré:', response.data);
      
      const owner = Array.isArray(response.data) ? response.data[0] : response.data;
      
      if (owner) {
        saveOwnerToCache(owner);
      }
      
      return owner;
      
    } catch (error: any) {
      console.error('❌ Erreur encoursowner:', error.response?.data || error.message);
      
      const cachedOwner = getOwnerFromCache();
      if (cachedOwner) {
        console.log('💾 Utilisation du cache pour owner courant');
        return cachedOwner;
      }
      
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch current owner');
    }
  }
);
// ✅ Create owner
export const createowner = createAsyncThunk<Owner, Partial<Owner>, { state: RootState }>(
  'owner/createOwner',
  async (ownerData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(
        `${baseUrlApi}/owners`,
        ownerData,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` },
          timeout: 5000
        }
      );
      console.log('✅ Owner créé:', response.data);
      dispatch(fetchowners());
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur createowner:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to create owner');
    }
  }
);

// ✅ Update owner
export const updateOwner = createAsyncThunk<
  Owner,
  { userData: Partial<Owner>; userId: number; imageFile?: File | null },
  { state: RootState }
>(
  'owner/updateOwner',
  async ({ userData, userId, imageFile }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;

      const response = await axios.put(
        `${baseUrlApi}/owners/${userId}`,
        userData,
        { headers: { Authorization: `bearer ${token}` }, timeout: 5000 }
      );
      console.log('✅ Owner mis à jour:', response.data);

      if (imageFile instanceof File) {
        const photoFormData = new FormData();
        photoFormData.append('photos', imageFile);
        await axios.post(`${baseUrlApi}/owners/${userId}/pictures`, photoFormData, {
          headers: { Authorization: `bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        console.log('✅ Photo owner uploadée');
      }

      dispatch(fetchowners());
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur updateOwner:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to update owner');
    }
  }
);

// ✅ Delete owner
export const deleteOwner = createAsyncThunk<number, number, { state: RootState }>(
  'owner/deleteOwner',
  async (ownerId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(
        `${baseUrlApi}/owners/${ownerId}`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` },
          timeout: 5000
        }
      );
      console.log('✅ Owner supprimé:', ownerId);
      dispatch(fetchowners());
      return ownerId;
    } catch (error: any) {
      console.error('❌ Erreur deleteOwner:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to delete owner');
    }
  }
);

const ownerSlice = createSlice({
  name: 'owner',
  initialState,
  reducers: {
    clearOwner: (state) => { 
      state.owner = null;
      localStorage.removeItem('currentOwner');
    },
    clearOwners: (state) => { 
      state.owners = []; 
    },
    resetOwnerState: () => {
      localStorage.removeItem('currentOwner');
      return initialState;
    },
    // ✅ NOUVEAU: Charger l'owner depuis le cache
    loadOwnerFromCache: (state) => {
      const cachedOwner = getOwnerFromCache();
      if (cachedOwner) {
        state.owner = cachedOwner;
        state.activeOwnerId = cachedOwner.id;
        console.log('💾 Owner chargé depuis le cache dans le state');
      }
    }
  },
  extraReducers: (builder) => {
    // ✅ Gestion des états de chargement
    [fetchowners, fetchowner, encoursowner, createowner, updateOwner, deleteOwner].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
          
          // ✅ En cas d'erreur, essayer de charger depuis le cache
          if (!state.owner) {
            const cachedOwner = getOwnerFromCache();
            if (cachedOwner) {
              state.owner = cachedOwner;
              state.activeOwnerId = cachedOwner.id;
              console.log('💾 Owner chargé depuis le cache après erreur');
            }
          }
        });
    });

    // ✅ Cas spécifiques fulfilled
    builder
      .addCase(fetchowners.fulfilled, (state, action) => {
        state.loading = false;
        state.owners = action.payload;
        console.log('📦 State owners mis à jour:', state.owners.length, 'owners');
      })
      .addCase(fetchowner.fulfilled, (state, action) => {
        state.loading = false;
        state.owner = action.payload;
        console.log('📦 State owner mis à jour:', state.owner?.id);
      })
      .addCase(encoursowner.fulfilled, (state, action) => {
        state.loading = false;
        state.owner = action.payload;
        state.activeOwnerId = action.payload?.id || null;
        console.log('📦 State owner courant mis à jour:', state.owner?.id);
      })
      .addCase(createowner.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateOwner.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteOwner.fulfilled, (state, action) => {
        state.loading = false;
        state.owners = state.owners.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearOwner, clearOwners, resetOwnerState, loadOwnerFromCache } = ownerSlice.actions;
export default ownerSlice.reducer;